import {prefetchIssueDebounced} from '@github-ui/issue-viewer/IssueViewerLoader'
import {testIdProps} from '@github-ui/test-id-props'
import {Box, Text} from '@primer/react'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {TitleValueWithContentType} from '../../../api/columns/contracts/storage'
import {ItemType} from '../../../api/memex-items/item-type'
import {isNumber, parseTitleNumber} from '../../../helpers/parsing'
import {useEnabledFeatures} from '../../../hooks/use-enabled-features'
import {useTableSidePanel} from '../../../hooks/use-side-panel'
import {type ColumnValue, hasValue} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {MemexItemIcon} from '../../common/memex-item-icon'
import {TextPlaceholder} from '../../common/placeholders'
import {InteractiveItemTitle} from '../../interactive-item-title'
import {BaseCell} from '../cells/base-cell'
import {useRecordCellRenderer} from '../performance-measurements'
import {withCellRenderer} from './cell-renderer'

type Props = Readonly<{
  currentValue: ColumnValue<TitleValueWithContentType>
  model: MemexItemModel
  isDisabled?: boolean
}>

export const LoadingTitleCell = () => (
  <BaseCell>
    <TextPlaceholder minWidth={80} maxWidth={200} {...testIdProps('placeholder')} />
  </BaseCell>
)

export const TitleRenderer = withCellRenderer<Props>(
  function TitleRenderer({currentValue, model, isDisabled}) {
    useRecordCellRenderer('TitleRenderer', model.id)
    const titleValue = hasValue(currentValue) ? currentValue.value : undefined
    const number = parseTitleNumber(titleValue)
    const {openPane} = useTableSidePanel()
    const {memex_new_issue_viewer, memex_issue_viewer_prefetch} = useEnabledFeatures()
    const environment = useRelayEnvironment()

    const onHover = useCallback(() => {
      if (memex_new_issue_viewer && memex_issue_viewer_prefetch && model.contentType === ItemType.Issue) {
        const issueData = model.getItemIdentifier()
        if (!issueData) return undefined
        const {number: issueNumber, owner, repo} = issueData
        prefetchIssueDebounced(environment, owner, repo, issueNumber)
      }
    }, [environment, memex_new_issue_viewer, memex_issue_viewer_prefetch, model])

    return (
      <BaseCell>
        <Box sx={{flexShrink: 0, mr: 2, display: 'flex'}}>
          <MemexItemIcon titleColumn={currentValue} />
        </Box>
        <InteractiveItemTitle
          model={model}
          isDisabled={isDisabled}
          currentValue={currentValue}
          openPane={openPane}
          onHover={onHover}
        />
        {isNumber(number) && (
          <Box sx={{flexShrink: 0, ml: 2, display: 'flex'}}>
            <Text sx={{color: 'fg.muted'}}> #{number}</Text>
          </Box>
        )}
      </BaseCell>
    )
  },
  {
    nullWhenRedacted: false,
  },
)

TitleRenderer.displayName = 'TitleRenderer'

try{ LoadingTitleCell.displayName ||= 'LoadingTitleCell' } catch {}